import { flatten } from 'lodash';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { r_chevron_down } from '../../../icons';
import getRenderable from '../../../lib/getRenderable';
import Icon from '../Icon';
import Popover from '../Popover';
import { Option, OptionWrapper } from './Option';
import { OPTION_CHILD_ERROR } from './constants';
import classes from './index.module.scss';

/**
 * Validates a provided Select child is usable.
 * @param {React.Component} child The child to validate.
 */
const validateChild = (child) => {
    if (Array.isArray(child)) {
        child.forEach(validateChild);
    } else if (!child.type || child.type !== Option) {
        throw new Error(OPTION_CHILD_ERROR);
    }
};

/**
 * Build the component's classes.
 * @param {Boolean} options.disabled Whether the component is disabled or not.
 * @param {String} options.className Custom class name passed by the consumer for the wrapper content.
 * @param {String} options.openClassName Custom class name passed by the consumer for the wrapper content when open.
 * @param {String} options.contentClassName Custom class name passed by the consumer for the content.
 * @param {String} options.boxClassName Custom class name passed by the consumer for the wrapper box popover.
 * @param {String} options.boxContentClassName Custom class name passed by the consumer for the box popover.
 * @param {String} options.containerClassName Custom class name passed by the consumer for the container popover.
 * @param {String} options.containerFullWidth Whether the container of the select should take full width of its parent.
 * @param {Boolean} options.isSuccess Whether or not the component is in a suceessful status.
 * @param {Boolean} options.isError Whether or not the component is in an error status.
 * @return {Object}
 */
const buildClasses = ({
    disabled,
    className,
    openClassName,
    contentClassName,
    boxClassName,
    boxContentClassName,
    containerClassName,
    valueClassName,
    containerFullWidth,
    isSuccess,
    isError
}) => ({
    wrapper: classnames(
        classes.selectWrapper,
        className,
        {
            [classes.wrapperDisabled]: disabled,
            [classes.fullWidth]: containerFullWidth
        }
    ),
    open: classnames(
        classes.popoverOpen,
        openClassName
    ),
    content: classnames(
        classes.select,
        contentClassName,
        {
            [classes.disabled]: disabled,
            [classes.success]: isSuccess,
            [classes.error]: isError
        }
    ),
    box: classnames(
        classes.popoverBox,
        boxClassName
    ),
    boxContent: classnames(
        classes.popoverBoxContent,
        boxContentClassName
    ),
    value: classnames(
        classes.value,
        valueClassName
    ),
    withPlaceholder: classes.withPlaceholder,
    caret: classes.caret,
    nativeWrapper: classnames(
        classes.nativeWrapper,
        {
            [classes.fullWidth]: containerFullWidth
        }
    ),
    container: containerClassName
});

/**
 * Serializes the provided React children into workable JSON representations of the options.
 * @param {React.Component[]} children The component's children.
 * @return {Option[]}
 */
const serializeOptions = (children) => children.map((option) => {
    validateChild(option);

    const { props } = option;

    return {
        value: props.value,
        label: props.children
    };
});

/**
 * Generates the menu options, enriching children with extra props.
 * @param {React.Component[]} options.children The component's children.
 * @param {Function} options.onChange A callback function for the "change" event.
 * @param {String} options.selectedValue The currently selected value.
 * @return {React.Component[]}
 */
const generateMenu = ({ children, onChange, selectedValue }) => children.map((child, index) => {
    const { props } = child;
    const enrichedProps = {
        index,
        selected: selectedValue === props.value,
        key: [index, props.value].join()
    };

    if (!props.disabled) {
        enrichedProps.onChange = onChange;
    }

    return getRenderable(OptionWrapper, props, enrichedProps);
});

/**
 * Finds a single option by the provided value.
 * @param {Option[]} options The component's options, serialized to JSON.
 * @param {String} targetValue The requested option's value.
 * @return {Option|null}
 */
const getOptionByValue = (options, targetValue) => options.find(({ value }) => value === targetValue) || {};

/**
 * Gets the component's initial value, taking into account the component's controlled/uncontrolled nature.
 * @param {Boolean} options.controlled Whether the component is controlled or not.
 * @param {Option[]} options.options The component's options, serialized to JSON.
 * @param {String} options.value The value provided as part of the component's props.
 * @param {String} options.defaultValue The default value provided as part of the component's props.
 * @return {Option}
 */
const getInitialValue = ({
    controlled,
    options,
    value,
    defaultValue
}) => {
    if (!controlled && typeof defaultValue === 'undefined') {
        return options[0];
    }

    const targetValue = controlled ? value : defaultValue;

    return getOptionByValue(options, targetValue);
};

/**
 * Gets the currently selected option, taking into account the component's controlled/uncontrolled nature.
 * @param {Boolean} options.controlled Whether the component is controlled or not.
 * @param {String} options.value The value provided as part of the component's props.
 * @param {Option[]} options.options The component's options, serialized to JSON.
 * @param {Option} options.selected The currently selected option, if the component is unconctrolled.
 * @return {Option}
 */
const getSelectedOption = ({
    controlled,
    value,
    options,
    selected
}) => {
    if (controlled) {
        return getOptionByValue(options, value);
    }

    return selected;
};

/**
 * @deprecated Please use Select from @fiverr-private/forms instead.
 */
const Select = ({
    children,
    disabled,
    value,
    defaultValue,
    placeholder,
    name,
    className,
    openClassName,
    contentClassName,
    boxClassName,
    boxContentClassName,
    containerClassName,
    valueClassName,
    boxFitContent,
    onChange,
    fullWidth,
    containerFullWidth,
    appendToBody,
    isSuccess,
    isError
}) => {
    const controlled = typeof value !== 'undefined';
    children = Array.isArray(children)
        ? flatten(children)
        : [children];
    const options = serializeOptions(children);

    const popoverRef = useRef();
    const [selected, setSelected] = useState(getInitialValue({
        options,
        controlled,
        value,
        defaultValue
    }));

    const handleChange = (e, index) => {
        if (disabled) { return; }

        const selected = options[index];

        if (!controlled) {
            setSelected(selected);
        }

        onChange(e, index, selected.value);
        popoverRef.current.close();
    };

    const selectedOption = getSelectedOption({
        controlled,
        options,
        value,
        selected
    });
    const showPlaceHolder = placeholder && selectedOption.value === undefined;
    const builtClasses = buildClasses({
        disabled,
        className,
        openClassName,
        contentClassName,
        boxClassName,
        boxContentClassName,
        containerClassName,
        valueClassName,
        containerFullWidth,
        isSuccess,
        isError
    });
    const { isTouch } = getContext();
    const optionElements = generateMenu({
        children,
        onChange: handleChange,
        selectedValue: selectedOption.value
    });

    const nativeSelectProps = {
        disabled,
        defaultValue,
        value,
        name,
        onChange: (e) => {
            const { selectedIndex, value } = e.target;
            const index = placeholder ? selectedIndex - 1 : selectedIndex;

            if (!controlled) {
                setSelected(options[index]);
            }

            onChange(e, index, value);
        }
    };

    if (isTouch) {
        return (
            <div className={builtClasses.nativeWrapper}>
                <select {...nativeSelectProps}
                    className={classnames(builtClasses.content, { [builtClasses.withPlaceholder]: showPlaceHolder })}>
                    {placeholder && <Option hidden disabled value="">{placeholder}</Option>}
                    {children}
                </select>
                <Icon size={11}
                    className={builtClasses.caret}>
                    {r_chevron_down}
                </Icon>
            </div>
        );
    }

    return (
        <Popover className={builtClasses.wrapper}
            contentClassName={builtClasses.content}
            fullWidth={fullWidth}
            boxFitContent={boxFitContent}
            openClassName={builtClasses.open}
            content={optionElements}
            boxClassName={builtClasses.box}
            boxContentClassName={builtClasses.boxContent}
            containerClassName={builtClasses.container}
            ref={popoverRef}
            position={Popover.POSITIONS.BOTTOM_LEFT}
            appendToBody={appendToBody}>
            <div className={classnames(builtClasses.value, { [builtClasses.withPlaceholder]: showPlaceHolder })}>
                {selectedOption.label || placeholder}
            </div>
            <Icon size={11}
                className={builtClasses.caret}>
                {r_chevron_down}
            </Icon>
            <input type="hidden" disabled={disabled} name={name} value={selectedOption.value || ''}/>
        </Popover>
    );
};

Select.displayName = 'Select';

Select.propTypes = {
    /**
     * The `<Select>`'s options.
     * `<Option>`'s `children` may **only be a number or a string**, due to [React's limitations with rendering elements inside `<option>`](https://github.com/facebook/react/issues/13586)
     */
    children: PropTypes.arrayOf(Option).isRequired,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Custom classes you wish to pass to the component when the popover is open.
     */
    openClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the component's content element.
     */
    contentClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the component's popover box element.
     */
    boxClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the component's popover box content element.
     */
    boxContentClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the wrapper of the component's popover box element.
     */
    containerClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the component's popover value element.
     */
    valueClassName: PropTypes.string,
    /**
     * Whether the popover box should take the full screen width or not.
     * This will only apply when the user agent device type is `mobile`
     */
    fullWidth: PropTypes.bool,
    /**
     * Whether the popover box should take at least the width of the content.
     */
    boxFitContent: PropTypes.bool,
    /**
     * Whether the component's popover box should be appended to the body.
     * Setting this to false will cause the popover box to be appended next to the trigger instead.
     */
    appendToBody: PropTypes.bool,
    /**
     * Whether or not the select is disabled.
     * A disabled select is not clickable, or hoverable.
     */
    disabled: PropTypes.bool,
    /**
     * The component's initial value.
     */
    defaultValue: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    /**
     * The component's selected value.
     */
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    /**
     * Similar to the native `<select>` `name`.
     */
    name: PropTypes.string,
    /**
     * A callback function to be executed when the component's value changes.
     */
    onChange: PropTypes.func,
    /**
     * A text that will be displayed before the user has selected an option.
     */
    placeholder: PropTypes.string,
    /**
     * Decides whether the select container should take the entire width of its parent.
     */
    containerFullWidth: PropTypes.bool,
    /**
     * Whether or not the component is in a suceessful status.
     * A success border is displayed.
     */
    isSuccess: PropTypes.bool,
    /**
    * Whether or not the component is in an error status.
    * An error border is displayed.
    */
    isError: PropTypes.bool
};

Select.defaultProps = {
    disabled: false,
    placeholder: '',
    onChange: () => null,
    boxFitContent: true,
    appendToBody: true,
    isSuccess: false,
    isError: false
};

Object.assign(Select, { Option });

export { Option };
export default Select;
